import { useEffect, useState } from 'react';
import { IconDownload, IconX } from '@tabler/icons-react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const detectarPlataforma = () => {
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isAndroid = /Android/.test(ua);
  const isMacSafari = /Macintosh/.test(ua) && /Safari/.test(ua) && !/Chrome|Chromium|Edg/.test(ua);
  return { isIOS, isAndroid, isMacSafari };
};

const instrucoesPorPlataforma = () => {
  const { isIOS, isAndroid, isMacSafari } = detectarPlataforma();
  if (isIOS) {
    return [
      'Toque no botão Compartilhar do Safari (quadrado com seta para cima).',
      'Role e escolha "Adicionar à Tela de Início".',
      'Confirme em "Adicionar". O ícone Saúde Connecting aparece na sua tela.',
    ];
  }
  if (isAndroid) {
    return [
      'Toque nos três pontos no canto superior do Chrome.',
      'Escolha "Instalar app" ou "Adicionar à tela inicial".',
      'Confirme. O ícone Saúde Connecting aparece na sua tela.',
    ];
  }
  if (isMacSafari) {
    return [
      'No menu Arquivo do Safari, escolha "Adicionar ao Dock".',
      'O atalho Saúde Connecting fica disponível no seu Dock.',
    ];
  }
  return [
    'No Chrome ou Edge, clique no ícone de instalação na barra de endereço (ou no menu de três pontos).',
    'Escolha "Instalar Saúde Connecting".',
    'Um atalho é criado no seu computador e o app abre em janela própria.',
  ];
};

export const InstalarAppButton = ({ variant = 'full', className = '' }: { variant?: 'full' | 'compact' | 'floating' | 'header'; className?: string }) => {
  const [promptDeferido, setPromptDeferido] = useState<BeforeInstallPromptEvent | null>(null);
  // Calculado já no estado inicial (não no useEffect): senão o botão aparece por um frame
  // e some logo depois em quem já abre o site pelo app instalado.
  const [instalado, setInstalado] = useState(() =>
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
  const [mostrarInstrucoes, setMostrarInstrucoes] = useState(false);

  useEffect(() => {
    const aoReceberPrompt = (e: Event) => {
      e.preventDefault();
      setPromptDeferido(e as BeforeInstallPromptEvent);
    };
    const aoInstalar = () => {
      setInstalado(true);
      setPromptDeferido(null);
    };

    window.addEventListener('beforeinstallprompt', aoReceberPrompt);
    window.addEventListener('appinstalled', aoInstalar);
    return () => {
      window.removeEventListener('beforeinstallprompt', aoReceberPrompt);
      window.removeEventListener('appinstalled', aoInstalar);
    };
  }, []);

  const handleClick = async () => {
    if (promptDeferido) {
      await promptDeferido.prompt();
      await promptDeferido.userChoice;
      setPromptDeferido(null);
      return;
    }
    setMostrarInstrucoes(true);
  };

  return (
    <>
      {variant === 'header' ? (
        <button
          onClick={handleClick}
          title="Instalar aplicativo"
          aria-label="Instalar aplicativo"
          className={`flex items-center gap-2 px-3 sm:px-4 py-2 bg-brand-500 hover:bg-brand-600 text-white font-semibold rounded-xl text-sm shrink-0 transition-colors ${className}`}
        >
          <IconDownload className="w-4 h-4" />
          <span className="hidden sm:inline">Instalar app</span>
        </button>
      ) : variant === 'floating' ? (
        <div className="group fixed bottom-6 right-6 z-40 flex items-center">
          <span className="pointer-events-none absolute right-full mr-3 whitespace-nowrap rounded-full bg-white px-4 py-2 text-sm font-medium text-gray-800 shadow-lg opacity-0 translate-x-2 transition-all duration-200 group-hover:opacity-100 group-hover:translate-x-0">
            Instale o app
          </span>
          <button
            onClick={handleClick}
            title="Instalar aplicativo"
            aria-label="Instalar aplicativo"
            className={`flex items-center justify-center w-14 h-14 bg-brand-500 hover:bg-brand-600 text-white rounded-full shadow-lg transition-colors ${className}`}
          >
            <IconDownload className="w-7 h-7" />
          </button>
        </div>
      ) : variant === 'compact' ? (
        <button
          onClick={handleClick}
          title="Instalar aplicativo"
          aria-label="Instalar aplicativo"
          className={`flex items-center justify-center w-9 h-9 border-2 border-white/80 text-white hover:bg-white/10 rounded-lg transition-colors shrink-0 ${className}`}
        >
          <IconDownload className="w-4 h-4" />
        </button>
      ) : (
        <button
          onClick={handleClick}
          className={`inline-flex items-center gap-2 px-5 py-2.5 bg-white border-2 border-brand-500 text-brand-700 hover:bg-brand-50 font-semibold rounded-xl transition-colors text-sm ${className}`}
        >
          <IconDownload className="w-4 h-4" />
          Instalar aplicativo
        </button>
      )}

      {mostrarInstrucoes && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={() => setMostrarInstrucoes(false)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 text-left" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Instalar Saúde Connecting</h3>
              <button onClick={() => setMostrarInstrucoes(false)} className="text-gray-400 hover:text-gray-600">
                <IconX className="w-5 h-5" />
              </button>
            </div>
            <ol className="list-decimal pl-5 space-y-2 text-sm text-gray-700">
              {(instalado ? ['O aplicativo já está instalado neste aparelho.'] : instrucoesPorPlataforma()).map((passo) => (
                <li key={passo}>{passo}</li>
              ))}
            </ol>
            <button
              onClick={() => setMostrarInstrucoes(false)}
              className="mt-5 w-full py-2.5 bg-brand-700 hover:bg-brand-800 text-white font-semibold rounded-xl transition-colors text-sm"
            >
              Entendi
            </button>
          </div>
        </div>
      )}
    </>
  );
};
